import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './ui';
import { PhotoViewerHost } from './PhotoViewer';
import { ShareSheetHost } from './ShareSheet';

// The app's own replacements for the browser's alert and confirm pop-ups, which
// show as system dialogs inside the iPhone app. Call toast() and confirmSheet() from anywhere;
// <FeedbackHost/>, mounted once in App, draws them.

let listener = null;
let nextId = 1;
const queue = [];
const emit = (event) => (listener ? listener(event) : queue.push(event));

// type: 'info' | 'success' | 'error'. Errors stay a little longer.
export function toast(text, { type = 'info', title, duration } = {}) {
  const id = nextId++;
  emit({ kind: 'toast', item: { id, text, type, title, duration: duration ?? (type === 'error' ? 6000 : 4000) } });
  return id;
}

// Resolves true when the user confirms, false on Cancel, the close button or Escape.
// With href, the confirm button is a link that opens it in a new tab, so pop-up
// blockers treat it as the user's own click.
// With choices ([{ label, value, href, download, icon }]), each choice is its own
// button or link, in order, and the sheet resolves with the tapped choice's value.
export function confirmSheet({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false, href, choices } = {}) {
  return new Promise(resolve => {
    emit({ kind: 'confirm', item: { id: nextId++, title, message, confirmLabel, cancelLabel, danger, href, choices, resolve } });
  });
}

function Toast({ item, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(() => onDismiss(item.id), item.duration);
    return () => clearTimeout(t);
  }, [item.id, item.duration, onDismiss]);
  return (
    <div className={`toast ${item.type}`} role={item.type === 'error' ? 'alert' : 'status'}>
      <span className="toast-icon" aria-hidden="true"><Icon name={item.type === 'error' ? 'alert' : item.type === 'success' ? 'check' : 'info'} size={20} /></span>
      <div className="toast-text">
        {item.title && <div className="toast-title">{item.title}</div>}
        <div className="toast-body">{item.text}</div>
      </div>
      <button className="toast-close" onClick={() => onDismiss(item.id)} aria-label="Dismiss"><Icon name="close" size={18} /></button>
    </div>
  );
}

function ConfirmSheet({ item, onDone }) {
  const safeRef = useRef(null);
  useEffect(() => { safeRef.current?.focus(); }, []);
  const finish = (ok) => { item.resolve(ok); onDone(item.id); };
  // Links close the sheet after the click, so the browser follows the link before the element is removed.
  const finishAfterLink = (ok) => setTimeout(() => finish(ok), 0);
  const confirmClass = `btn full ${item.danger ? 'btn-danger' : ''}`;
  return (
    <div className="modal-overlay confirm-overlay" onClick={e => { if (e.target === e.currentTarget) finish(false); }}>
      <div className="modal confirm-sheet" role="alertdialog" aria-modal="true" aria-labelledby={`confirm-title-${item.id}`}>
        <div className="modal-head">
          <h3 id={`confirm-title-${item.id}`}>{item.title}</h3>
          <button className="modal-close" onClick={() => finish(false)} aria-label="Close"><Icon name="close" size={20} /></button>
        </div>
        {item.message && <div className="confirm-message">{item.message}</div>}
        <div className="confirm-actions">
          {item.choices ? item.choices.map((c, i) => {
            const cls = i === 0 ? 'btn full' : 'btn-secondary full';
            const ref = i === 0 ? safeRef : undefined;
            const done = () => finish(c.value ?? true);
            const body = <>{c.icon && <Icon name={c.icon} size={20} />}{c.label}</>;
            if (!c.href) return <button key={i} className={cls} onClick={done} ref={ref}>{body}</button>;
            const linkProps = c.download ? { download: c.download } : { target: '_blank', rel: 'noopener noreferrer' };
            return <a key={i} className={cls} href={c.href} {...linkProps} onClick={() => finishAfterLink(c.value ?? true)} ref={ref}>{body}</a>;
          })
          : item.href
            ? <a className={confirmClass} href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => finishAfterLink(true)} ref={item.danger ? undefined : safeRef}>{item.confirmLabel}</a>
            : <button className={confirmClass} onClick={() => finish(true)} ref={item.danger ? undefined : safeRef}>{item.confirmLabel}</button>}
          <button className="btn-secondary full" onClick={() => finish(false)} ref={item.danger ? safeRef : undefined}>{item.cancelLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function FeedbackHost() {
  const [toasts, setToasts] = useState([]);
  const [confirms, setConfirms] = useState([]);
  const dismissRef = useRef(id => setToasts(prev => prev.filter(t => t.id !== id)));

  useEffect(() => {
    const handle = ({ kind, item }) => {
      if (kind === 'toast') setToasts(prev => [...prev, item]);
      else setConfirms(prev => [...prev, item]);
    };
    listener = handle;
    queue.splice(0).forEach(handle);
    return () => { if (listener === handle) listener = null; };
  }, []);

  return (
    <>
      <PhotoViewerHost />
      <ShareSheetHost />
      {confirms.length > 0 && <ConfirmSheet key={confirms[0].id} item={confirms[0]} onDone={id => setConfirms(prev => prev.filter(c => c.id !== id))} />}
      {toasts.length > 0 && (
        <div className="toast-stack">
          {toasts.map(t => <Toast key={t.id} item={t} onDismiss={dismissRef.current} />)}
        </div>
      )}
    </>
  );
}
